/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from "react";
import useSnackbar from "../hooks/useSnackbar";
import {
    getClinic,
    putClinic,
    getDoctorExclusions,
    saveDoctorExclusions,
    getBranchExclusions,
    saveBranchExclusions,
    getBranches,
    getMisBranches,
} from "../http/clinicAPI";
import { changeForDB, changeForEditor } from "../helpers/editorFunc";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import axios from "axios";
import MySnackbar from "./MySnackbar";
import Editor from "./Editor";
import { Context } from "../index";
import InfoIcon from "@mui/icons-material/Info";
import SmsAeroSettings from "./SmsAeroSettings";

const ClinicSettings = () => {
    const snack = useSnackbar(false, "Ошибка");
    const { features } = useContext(Context);
    const [logo, setLogo] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const [clinicSettings, setClinicSettings] = useState({
        name: "",
        appointmentLink: "",
        daysBeforeBurning: "",
        daysToNoticeAboutBurning: "",
        daysBeforeBirthday: "",
        birthdaySwitch: false,
        pointsChargesCount: "",
        referralChargesCondition: "",
        appointmentActionDays: "",
        description: "",
        disableUserBirthday: false,
        isNotificationExclusionEnabled: false,
    });

    const [availableDoctors, setAvailableDoctors] = useState([]);
    const [selectedDoctors, setSelectedDoctors] = useState([]);
    const [loadingDoctors, setLoadingDoctors] = useState(false);

    const [availableBranches, setAvailableBranches] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);

    const [clinicPoints, setClinicPoints] = useState({
        pointsForRegistration: "",
        pointsPercentForPay: "",
        pointsPercentForPayTo: "",
        pointsForRecommend: "",
        pointsForBirthday: "",
        pointsForReviewBranch: "",
        pointsForReviewDoctor: "",
        pointsFixForPay: "",
        pointsMaxPercentForPayment: "",
    });

    const [clinicNotifications, setClinicNotifications] = useState({
        is_appointment_reminder: false,
        is_birthday_greeting: false,
        is_post_appointment: false,
    });

    const [loadingClinic, setLoadingClinic] = useState(true);

    const [burningError, setBurningError] = useState(false);

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchClinicData = async () => {
            try {
                const data = await getClinic();
                setClinicPoints(data.points || { ...clinicPoints });
                setClinicNotifications(
                    data.notificationSettings || { ...clinicNotifications },
                );
                setClinicSettings({
                    ...data,
                    description: changeForEditor(data.description || ""),
                });

                // Всегда загружаем список докторов и исключений
                await loadDoctorsData();

                // Загружаем список филиалов и исключений
                await loadBranchesData();
            } catch (error) {
                snack.handleChange(true, "Ошибка при загрузке данных клиники");
            } finally {
                setLoadingClinic(false);
            }
        };

        fetchClinicData();
    }, []);

    const loadDoctorsData = async () => {
        try {
            setLoadingDoctors(true);

            let exclusions = await getDoctorExclusions();
            exclusions = exclusions.map((ex) => ex.dataValues || ex);

            // Загружаем список докторов из внешней системы
            const clinicData = await getClinic();

            const apiKey =
                clinicData.systemSecrets?.apiKey || clinicData.apiKey;
            const webApiAddress =
                clinicData.systemSecrets?.webApiAddress ||
                clinicData.webApiAddress;

            if (!apiKey || !webApiAddress) {
                if (exclusions.length > 0) {
                    const savedDoctors = exclusions.map((ex) => ({
                        id: String(ex.externalDoctorId),
                        name: ex.doctorName,
                    }));
                    setAvailableDoctors(savedDoctors);
                    setSelectedDoctors(savedDoctors);
                }
                return;
            }

            const response = await axios.post(
                `${webApiAddress}/api/public/getUsers`,
                null,
                {
                    params: { api_key: apiKey },
                },
            );

            if (response.data && response.data.data) {
                // Фильтруем только докторов
                const doctors = response.data.data
                    .filter(
                        (user) =>
                            user.role_names &&
                            user.role_names.includes("doctor"),
                    )
                    .map((user) => ({
                        id: String(user.id),
                        name: user.name || "",
                    }));

                setAvailableDoctors(doctors);

                // Фильтруем выбранных докторов на основе исключений
                const selectedDoctorsList = doctors.filter((doctor) => {
                    return exclusions.some((exclusion) => {
                        return (
                            String(exclusion.externalDoctorId) ===
                            String(doctor.id)
                        );
                    });
                });
                setSelectedDoctors(selectedDoctorsList);
            }
        } catch (error) {
            console.error("Ошибка загрузки докторов:", error);
            try {
                let exclusions = await getDoctorExclusions();
                // Преобразуем Sequelize объекты в plain objects
                exclusions = exclusions.map((ex) => ex.dataValues || ex);
                if (exclusions.length > 0) {
                    const savedDoctors = exclusions.map((ex) => ({
                        id: String(ex.externalDoctorId),
                        name: ex.doctorName,
                    }));
                    setAvailableDoctors(savedDoctors);
                    setSelectedDoctors(savedDoctors);
                }
            } catch (e) {
                console.error("Ошибка загрузки исключений:", e);
            }
        } finally {
            setLoadingDoctors(false);
        }
    };

    const loadBranchesData = async () => {
        try {
            setLoadingBranches(true);

            // Получаем исключения филиалов из БД
            let exclusions = await getBranchExclusions();
            exclusions = exclusions.map((ex) => ex.dataValues || ex);

            // Загружаем локальные филиалы из базы данных
            const branchesData = await getMisBranches();

            if (branchesData && branchesData.length > 0) {
                const branches = branchesData.map((branch) => ({
                    externalId: branch.misId ? String(branch.misId) : null,
                    name: branch.name || "",
                }));

                setAvailableBranches(branches);

                // Фильтруем выбранные филиалы на основе исключений
                const selectedBranchesList = branches.filter((branch) => {
                    return exclusions.some((exclusion) => {
                        return (
                            String(exclusion.externalBranchId) ===
                            String(branch.externalId)
                        );
                    });
                });
                setSelectedBranches(selectedBranchesList);
            } else if (exclusions.length > 0) {
                // Если нет филиалов, но есть сохраненные исключения
                const savedBranches = exclusions.map((ex) => ({
                    externalId: ex.externalBranchId
                        ? String(ex.externalBranchId)
                        : null,
                    name: ex.branchName,
                }));
                setAvailableBranches(savedBranches);
                setSelectedBranches(savedBranches);
            }
        } catch (error) {
            console.error("Ошибка загрузки филиалов:", error);
        } finally {
            setLoadingBranches(false);
        }
    };

    const saveClinic = async () => {
        if (burningChecked) {
            const val = Number(clinicSettings.daysBeforeBurning);
            if (!val || isNaN(val) || val <= 0) {
                setBurningError(true);
                return;
            }
        }
        setSaving(true);
        try {
            const formData = new FormData();
            const appendIfValue = (key, value) => {
                if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            };

            // Добавляем текстовые данные
            appendIfValue("name", clinicSettings.name);
            appendIfValue("appointmentLink", clinicSettings.appointmentLink);
            appendIfValue(
                "daysBeforeBurning",
                clinicSettings.daysBeforeBurning,
            );
            appendIfValue(
                "daysBeforeBirthday",
                clinicSettings.daysBeforeBirthday,
            );
            appendIfValue("birthdaySwitch", clinicSettings.birthdaySwitch);
            appendIfValue(
                "pointsChargesCount",
                clinicSettings.pointsChargesCount,
            );
            appendIfValue(
                "referralChargesCondition",
                clinicSettings.referralChargesCondition,
            );
            appendIfValue(
                "appointmentActionDays",
                clinicSettings.appointmentActionDays,
            );
            appendIfValue(
                "description",
                changeForDB(clinicSettings.description),
            );
            appendIfValue(
                "isAgreementEnabled",
                clinicSettings.isAgreementEnabled || false,
            );
            appendIfValue("agreementText", clinicSettings.agreementText || "");
            appendIfValue(
                "isNotificationExclusionEnabled",
                clinicSettings.isNotificationExclusionEnabled || false,
            );

            // Добавляем настройки уведомлений
            appendIfValue(
                "notificationSettings",
                JSON.stringify(clinicNotifications),
            );

            // Добавляем очки клиники
            appendIfValue("points", JSON.stringify(clinicPoints));

            // Если есть логотип, добавляем его
            if (logo) {
                formData.append("logo", logo); // 'logo' - это имя поля, которое ожидает сервер
            }

            appendIfValue(
                "disableUserBirthday",
                clinicSettings.disableUserBirthday,
            );

            await putClinic(formData); // Предполагается, что putClinic поддерживает FormData

            // Сохраняем исключения докторов (если чекбокс включен - сохраняем список, если выключен - очищаем)
            await saveDoctorExclusions(
                clinicSettings.isNotificationExclusionEnabled
                    ? selectedDoctors
                    : [],
            );

            // Сохраняем исключения филиалов (если чекбокс включен - сохраняем список, если выключен - очищаем)
            await saveBranchExclusions(
                clinicSettings.isNotificationExclusionEnabled
                    ? selectedBranches
                    : [],
            );

            snack.handleChange(true, "Сохранено", "success");
        } catch (error) {
            snack.handleChange(true, "Ошибка при сохранении данных клиники");
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (event) => {
        const files = Array.from(event.target.files);
        if (files[0].size < 3145728) {
            handleLogoUpload(files[0]);
        } else {
            snack.handleChange(true, "Размер фото должен быть менее 3 мб");
        }
    };

    const handleLogoUpload = (file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function (e) {
            const image = new Image();
            image.src = e.target.result;

            image.onload = function () {
                const { height, width } = this;
                if (height > 2550 || width > 2550) {
                    snack.handleChange(
                        true,
                        "Стороны изображения должны быть менее 2550",
                    );
                } else {
                    setLogo(file);
                    setLogoUrl(URL.createObjectURL(file));
                }
            };
        };
    };

    const burningChecked =
        clinicSettings.daysBeforeBurning !== 0 &&
        clinicSettings.daysBeforeBurning !== null &&
        clinicSettings.daysBeforeBurning !== undefined;

    return (
        <Box sx={{ "& .MuiTextField-root": { m: 1, ml: 0 }, p: 0 }}>
            <MySnackbar {...snack} />
            {loadingClinic ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100vh",
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Основные данные
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                flexWrap: { xs: "wrap", md: "nowrap" },
                                alignItems: "stretch",
                                mb: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: "100%", md: 320 },
                                    padding: "8px",
                                    display: "flex",
                                }}
                            >
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleChange}
                                    style={{ display: "none" }}
                                    id="upload-logo"
                                />
                                <label
                                    htmlFor="upload-logo"
                                    style={{ width: "100%" }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            border: "2px dashed grey",
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "grey.100",
                                            cursor: "pointer",
                                            textAlign: "center",
                                            p: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: "medium" }}
                                            >
                                                Загрузить логотип админ.панели
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ mt: 1 }}
                                            >
                                                Перетащите изображение сюда или
                                                нажмите для загрузки
                                            </Typography>
                                        </Box>
                                    </Box>
                                </label>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 280 }}>
                                <TextField
                                    label="Название клиники в админ.панели"
                                    error={clinicSettings?.name?.length === 0}
                                    value={clinicSettings.name || ""}
                                    onChange={(e) =>
                                        setClinicSettings({
                                            ...clinicSettings,
                                            name: e.target.value,
                                        })
                                    }
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                />
                                <Box sx={{ mt: 2 }}>
                                    Описание, которое будет отображаться в боте
                                    по нажатию на кнопку “О клинике”
                                    <Editor
                                        value={clinicSettings.description || ""}
                                        setValue={(value, delta, source) => {
                                            if (value.length > 800) {
                                                snack.handleChange(
                                                    true,
                                                    "Количество символов до 800",
                                                );
                                                return;
                                            }
                                            if (source === "user") {
                                                setClinicSettings({
                                                    ...clinicSettings,
                                                    description: value,
                                                });
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Онлайн-запись
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            label="Ссылка онлайн записи в вашей клиники"
                            value={clinicSettings.appointmentLink || ""}
                            onChange={(e) =>
                                setClinicSettings({
                                    ...clinicSettings,
                                    appointmentLink: e.target.value,
                                })
                            }
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>

                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Согласие ОПД
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={
                                        clinicSettings.isAgreementEnabled ||
                                        false
                                    }
                                    onChange={(e) =>
                                        setClinicSettings({
                                            ...clinicSettings,
                                            isAgreementEnabled:
                                                e.target.checked,
                                        })
                                    }
                                />
                            }
                            label="Включить согласие ОПД"
                        />
                        {clinicSettings.isAgreementEnabled && (
                            <Box sx={{ mt: 2 }}>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1 }}
                                >
                                    Пользователь видит и принимает это
                                    соглашение в момент отправки номера телефона
                                    в Telegram-боте (при регистрации или при
                                    повторном сопоставлении карты)
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1 }}
                                >
                                    Не используйте специальные символы в тексте
                                    ({"<, >, |, `"})
                                </Typography>
                                <TextField
                                    label="Текст соглашения"
                                    value={clinicSettings.agreementText || ""}
                                    onChange={(e) =>
                                        setClinicSettings({
                                            ...clinicSettings,
                                            agreementText: e.target.value,
                                        })
                                    }
                                    fullWidth
                                    multiline
                                    rows={6}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                        )}
                    </Box>

                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Исключения уведомлений
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={
                                        clinicSettings.isNotificationExclusionEnabled ||
                                        false
                                    }
                                    onChange={(e) => {
                                        setClinicSettings({
                                            ...clinicSettings,
                                            isNotificationExclusionEnabled:
                                                e.target.checked,
                                        });
                                        // Если выключаем чекбокс, очищаем выбранных докторов и филиалов
                                        if (!e.target.checked) {
                                            setSelectedDoctors([]);
                                            setSelectedBranches([]);
                                        }
                                    }}
                                />
                            }
                            label="Исключить уведомления"
                        />
                        {clinicSettings.isNotificationExclusionEnabled && (
                            <Box sx={{ mt: 2 }}>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1 }}
                                >
                                    При записи к этому доктору пользователь не
                                    получает уведомление
                                </Typography>
                                {loadingDoctors ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            p: 2,
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <Autocomplete
                                        multiple
                                        options={availableDoctors}
                                        getOptionLabel={(option) => option.name}
                                        value={selectedDoctors}
                                        onChange={(event, newValue) => {
                                            setSelectedDoctors(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label="Выберите докторов"
                                                placeholder="Начните вводить имя..."
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        noOptionsText="Нет доступных докторов"
                                        sx={{ width: "100%" }}
                                    />
                                )}

                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1, mt: 3 }}
                                >
                                    Выберите филиалы, для которых уведомления
                                    отправляться не будут
                                </Typography>
                                {loadingBranches ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            p: 2,
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <Autocomplete
                                        multiple
                                        options={availableBranches}
                                        getOptionLabel={(option) => option.name}
                                        value={selectedBranches}
                                        onChange={(event, newValue) => {
                                            setSelectedBranches(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label="Выберите филиалы"
                                                placeholder="Начните вводить название..."
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) =>
                                            option.externalId ===
                                            value.externalId
                                        }
                                        noOptionsText="Нет доступных филиалов"
                                        sx={{ width: "100%" }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>

                    <Typography m={2} variant="h5">
                        Настройки программы лояльности
                    </Typography>
                    <Box
                        sx={{
                            "& .MuiTextField-root": { width: "30%" },
                            mb: 2,
                            p: 0,
                        }}
                    >
                        <TextField
                            label="Начисление баллов за регистрацию"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={clinicPoints.pointsForRegistration || ""}
                            onChange={(e) =>
                                setClinicPoints({
                                    ...clinicPoints,
                                    pointsForRegistration: e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Начисление баллов за отзыв на филиал"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={clinicPoints.pointsForReviewBranch || ""}
                            onChange={(e) =>
                                setClinicPoints({
                                    ...clinicPoints,
                                    pointsForReviewBranch: e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Начисление баллов за отзыв на врача"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={clinicPoints.pointsForReviewDoctor || ""}
                            onChange={(e) =>
                                setClinicPoints({
                                    ...clinicPoints,
                                    pointsForReviewDoctor: e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Начисление баллов за рекомендацию"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={clinicPoints.pointsForRecommend || ""}
                            onChange={(e) =>
                                setClinicPoints({
                                    ...clinicPoints,
                                    pointsForRecommend: e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                    {/* Процент, который можно оплатить бонусами */}
                    <Box
                        sx={{
                            "& .MuiTextField-root": { width: "30%" },
                            mb: 2,
                            p: 0,
                        }}
                    >
                        <TextField
                            label="Процент от счета, который можно оплатить бонусами"
                            fullWidth
                            type="number"
                            variant="outlined"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        %
                                    </InputAdornment>
                                ),
                                inputProps: {
                                    min: 0,
                                    max: 100,
                                },
                            }}
                            value={
                                clinicPoints.pointsMaxPercentForPayment || ""
                            }
                            onChange={(e) => {
                                let perctent = parseInt(e.target.value);
                                if (!perctent) perctent = 0;
                                if (perctent >= 0 && perctent <= 100) {
                                    setClinicPoints({
                                        ...clinicPoints,
                                        pointsMaxPercentForPayment:
                                            e.target.value,
                                    });
                                }
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Процент кэшбэка от суммы счёта (от)"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={clinicPoints.pointsPercentForPay || ""}
                            onChange={(e) =>
                                setClinicPoints({
                                    ...clinicPoints,
                                    pointsPercentForPay:
                                        e.target.value === ''
                                            ? null
                                            : e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Процент кэшбэка от суммы счёта (до)"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={clinicPoints.pointsPercentForPayTo || ""}
                            onChange={(e) =>
                                setClinicPoints({
                                    ...clinicPoints,
                                    pointsPercentForPayTo:
                                        e.target.value === ''
                                            ? null
                                            : e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Процент кэшбэка от суммы счёта (до)"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={clinicPoints.pointsPercentForPayTo || ""}
                            onChange={(e) =>
                                setClinicPoints({
                                    ...clinicPoints,
                                    pointsPercentForPayTo: e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={
                                        clinicSettings.pointsChargesCount !=
                                            null || false
                                    }
                                    onChange={(event) =>
                                        setClinicSettings({
                                            ...clinicSettings,
                                            pointsChargesCount: event.target
                                                .checked
                                                ? 5
                                                : null,
                                        })
                                    }
                                />
                            }
                            label="Ограничить начисление бонусов за рекомендацию в месяц"
                        />
                        {clinicSettings.pointsChargesCount != null && (
                            <>
                                <TextField
                                    label="Получать бонусы могут не более указанного количества в месяц"
                                    fullWidth
                                    type="number"
                                    variant="outlined"
                                    value={
                                        clinicSettings.pointsChargesCount || ""
                                    }
                                    onChange={(e) =>
                                        setClinicSettings({
                                            ...clinicSettings,
                                            pointsChargesCount: e.target.value,
                                        })
                                    }
                                    InputLabelProps={{ shrink: true }}
                                />
                            </>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={burningChecked}
                                    onChange={(event) => {
                                        if (event.target.checked) {
                                            setClinicSettings({
                                                ...clinicSettings,
                                                daysBeforeBurning:
                                                    clinicSettings.daysBeforeBurning >
                                                    0
                                                        ? clinicSettings.daysBeforeBurning
                                                        : 30,
                                            });
                                        } else {
                                            setClinicSettings({
                                                ...clinicSettings,
                                                daysBeforeBurning: 0,
                                            });
                                        }
                                    }}
                                />
                            }
                            label="Срок действия бонусов, до сгорания"
                        />
                        {burningChecked && (
                            <TextField
                                label="Через сколько дней"
                                type="number"
                                size="small"
                                variant="outlined"
                                sx={{ width: 180 }}
                                value={
                                    clinicSettings.daysBeforeBurning === 0
                                        ? ""
                                        : clinicSettings.daysBeforeBurning
                                }
                                error={burningError}
                                helperText={
                                    burningError
                                        ? "Введите положительное число"
                                        : ""
                                }
                                onChange={(e) => {
                                    setClinicSettings({
                                        ...clinicSettings,
                                        daysBeforeBurning: e.target.value,
                                    });
                                    setBurningError(false);
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {features.features.canDisableUserBirthday && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                clinicSettings.disableUserBirthday
                                            }
                                            onChange={(event) => {
                                                setClinicSettings({
                                                    ...clinicSettings,
                                                    disableUserBirthday:
                                                        event.target.checked &&
                                                        features.features
                                                            .canDisableUserBirthday,
                                                });
                                            }}
                                        />
                                    }
                                    label="Отключить дни рождения пользователей"
                                    sx={{ marginRight: 2 }}
                                />
                                <Tooltip
                                    title={
                                        "Пациенты не будут указывать дату рождения. Некоторые функции, связанные с днями рождения, работать не будут."
                                    }
                                    arrow
                                >
                                    <InfoIcon
                                        sx={{ opacity: 0.75 }}
                                        fontSize="small"
                                    />
                                </Tooltip>
                            </>
                        )}
                    </Box>
                    {/* <Typography m={2} variant='h5'>
            Подтверждение/отмена записей на прием
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  clinicSettings.appointmentActionDays ||
                  clinicSettings.appointmentActionDays === '' ||
                  false
                }
                onChange={(event) =>
                  setClinicSettings({
                    ...clinicSettings,
                    appointmentActionDays: event.target.checked ? 1 : null,
                  })
                }
              />
            }
            label='Разрешить пациентам подверждать/отменять свои записи?'
          /> */}
                    {/* {(clinicSettings.appointmentActionDays ||
            clinicSettings.appointmentActionDays === '') && (
            <Box sx={{ '& .MuiTextField-root': { width: '30%' }, mb: 2, p: 0 }}>
              <TextField
                label='За сколько дней до приема?'
                fullWidth
                type='number'
                variant='outlined'
                value={clinicSettings.appointmentActionDays || ''}
                onChange={(e) =>
                  setClinicSettings({
                    ...clinicSettings,
                    appointmentActionDays: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )} */}
                    {/* <Typography m={2} variant='h5'>
            Сгорание баллов
          </Typography>
          <Box sx={{ '& .MuiTextField-root': { width: '30%' }, mb: 2, p: 0 }}>
            <TextField
              label='Количество дней для сгорания баллов'
              fullWidth
              type='number'
              variant='outlined'
              value={clinicSettings.daysBeforeBurning || ''}
              onChange={(e) =>
                setClinicSettings({
                  ...clinicSettings,
                  daysBeforeBurning: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label='Количество дней от даты сгорания баллов до уведомления о сгорании баллов'
              fullWidth
              type='number'
              variant='outlined'
              value={clinicSettings.daysToNoticeAboutBurning || ''}
              onChange={(e) =>
                setClinicSettings({
                  ...clinicSettings,
                  daysToNoticeAboutBurning: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box> */}
                    {/* <Typography m={2} variant='h5'>
            Настройки уведомлений
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={clinicNotifications.is_appointment_reminder || false}
                onChange={(event) =>
                  setClinicNotifications({
                    ...clinicNotifications,
                    is_appointment_reminder: event.target.checked,
                  })
                }
              />
            }
            label='Напоминания о приёме'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={clinicNotifications.is_post_appointment || false}
                onChange={(event) =>
                  setClinicNotifications({
                    ...clinicNotifications,
                    is_post_appointment: event.target.checked,
                  })
                }
              />
            }
            label='Уведомление о новой записи на приём'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={clinicNotifications.is_birthday_greeting || false}
                onChange={(event) =>
                  setClinicNotifications({
                    ...clinicNotifications,
                    is_birthday_greeting: event.target.checked,
                  })
                }
              />
            }
            label='Поздравления с днём рождения'
          /> */}

                    <SmsAeroSettings />
                    <Button
                        variant="contained"
                        disabled={
                            saving ||
                            loadingClinic ||
                            clinicSettings?.name?.length === 0 ||
                            burningError
                        }
                        onClick={saveClinic}
                        sx={{ borderRadius: "15px", mt: 3 }}
                    >
                        {saving ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Сохранить"
                        )}
                    </Button>
                </>
            )}
        </Box>
    );
};

export default ClinicSettings;
